<!-- views/AboutView.vue -->
<template>
  <div class="about-view">
    <div class="about-container">
      <div class="about-header">
        <h1>About MyHarf</h1>
        <p>Learn. Create. Showcase.</p>
      </div>

      <div class="about-content">
        <section class="mission-section">
          <h2>Our Mission</h2>
          <p>
            MyHarf is an educational platform designed to empower creators and
            learners in Iraq and beyond. We believe that knowledge should be
            accessible, and that every expert has the potential to become an
            educator.
          </p>
          <p>
            Our platform combines the best of learning and social networking,
            creating a space where your courses become your professional
            portfolio.
          </p>
        </section>

        <section class="features-section">
          <h2>What We Offer</h2>
          <div class="features-grid">
            <div class="feature">
              <span class="icon">📚</span>
              <h3>Course Creation</h3>
              <p>Create and share courses with video and article content</p>
            </div>
            <div class="feature">
              <span class="icon">👥</span>
              <h3>Social Learning</h3>
              <p>Connect with learners and creators in your network</p>
            </div>
            <div class="feature">
              <span class="icon">💼</span>
              <h3>Portfolio Building</h3>
              <p>Your courses become your professional showcase</p>
            </div>
            <div class="feature">
              <span class="icon">🌍</span>
              <h3>Global Community</h3>
              <p>
                Join a growing community of Iraqi and international educators
              </p>
            </div>
          </div>
        </section>

        <section class="stats-section">
          <h2>Our Impact</h2>
          <div class="stats-grid">
            <div class="stat">
              <span class="number">1,000+</span>
              <span class="label">Active Learners</span>
            </div>
            <div class="stat">
              <span class="number">50+</span>
              <span class="label">Course Creators</span>
            </div>
            <div class="stat">
              <span class="number">200+</span>
              <span class="label">Courses</span>
            </div>
            <div class="stat">
              <span class="number">95%</span>
              <span class="label">Satisfaction Rate</span>
            </div>
          </div>
        </section>

        <section class="team-section">
          <h2>Our Team</h2>
          <div class="team-grid">
            <div class="team-member">
              <img
                src="https://ui-avatars.com/api/?name=Founder&background=FF8000&color=fff&size=120"
                alt="Founder"
              />
              <h3>Ahmed Al-Jubouri</h3>
              <p>Founder & CEO</p>
              <div class="social-links">
                <a href="#" target="_blank">🐙</a>
                <a href="#" target="_blank">💼</a>
                <a href="#" target="_blank">🐦</a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$primary-orange: #ff8000;
$primary-blue: #001829;

.about-view {
  margin-top: 72px;
  min-height: 100vh;
  background: #f0f2f5;
}

.about-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.about-header {
  background: linear-gradient(135deg, $primary-blue, #003355);
  color: #fff;
  padding: 60px 40px;
  border-radius: 16px;
  text-align: center;
  margin-bottom: 40px;

  h1 {
    font-size: 48px;
    margin: 0 0 8px 0;
  }

  p {
    font-size: 20px;
    opacity: 0.9;
    margin: 0;
  }
}

.about-content {
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  section {
    margin-bottom: 48px;

    &:last-child {
      margin-bottom: 0;
    }

    h2 {
      font-size: 28px;
      color: #1a1a1a;
      margin: 0 0 16px 0;
    }
  }
}

.mission-section {
  p {
    font-size: 16px;
    line-height: 1.8;
    color: #495057;
    margin: 0 0 12px 0;
  }
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;

  .feature {
    text-align: center;
    padding: 24px;
    background: #f8f9fa;
    border-radius: 12px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    }

    .icon {
      font-size: 40px;
      display: block;
      margin-bottom: 12px;
    }

    h3 {
      margin: 0 0 8px 0;
      color: #1a1a1a;
    }

    p {
      margin: 0;
      color: #868e96;
      font-size: 14px;
    }
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;

  .stat {
    text-align: center;
    padding: 24px;
    background: #f8f9fa;
    border-radius: 12px;

    .number {
      display: block;
      font-size: 32px;
      font-weight: 700;
      color: $primary-orange;
    }

    .label {
      font-size: 14px;
      color: #868e96;
    }
  }
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 24px;

  .team-member {
    text-align: center;

    img {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      object-fit: cover;
      border: 3px solid $primary-orange;
      margin-bottom: 12px;
    }

    h3 {
      margin: 0 0 4px 0;
      color: #1a1a1a;
    }

    p {
      margin: 0 0 8px 0;
      color: #868e96;
      font-size: 14px;
    }

    .social-links {
      display: flex;
      justify-content: center;
      gap: 12px;

      a {
        font-size: 20px;
        text-decoration: none;
        transition: all 0.2s ease;

        &:hover {
          transform: scale(1.2);
        }
      }
    }
  }
}
</style>
